import { useQueryClient } from '@tanstack/react-query';
import type { MenuProps } from 'antd';
import { Button, Dropdown, Modal, Spin } from 'antd';
import { useEffect, useRef, useState } from 'react';

import {
  AppShell,
  Link,
  Outlet,
  useLocation,
  useNavigate,
  useScrollToTopOnNavigate,
  useSelectedKeyFromPath,
} from '@suntex.core/nav';
import type { LoginId } from '@suntex.fim/api-client';
import { useAuth } from '@suntex.fim/auth/react';
import { EntityLevel, MetaKey } from '@suntex.fim/enums';
import { getFlags } from '@suntex.fim/flags';
import { resetDeviceRotateState, useFramework } from '@suntex.fim/framework';
import { SendMessage, useNotificationCount } from '@suntex.fim/messages';
import { useOptionalPlayerContext } from '@suntex.fim/player';
import { useOptionalTeamContext } from '@suntex.fim/team';
import { Tooltip, breakpoints, showErrorMessage } from '@suntex.fim/ui';

import { EntitySwitcher } from '../EntitySwitcher';
import { useEntityContext } from '../context/EntityContext';
import { navIcons } from '../icons';
import { getCollapsedLogo, getDesktopLogo } from '../logo';
import { buildMenusForLevel } from '../menus';
import { getHomePath } from '../routing/handleNavigation';
import { entityPath } from '../utils/paths';

import './CommonLayout.scss';

declare global {
  interface Window {
    isCommonLayoutReady?: boolean;
  }
}

/** Two-letter initials for the profile avatar, skipping common honorifics. */
function getInitials(displayName: string): string {
  if (!displayName) return '';
  const excluded = new Set(['mr', 'mrs', 'ms', 'dr']);
  const words = displayName
    .trim()
    .replace(/[.,]/g, '')
    .replace(/\s+/g, ' ')
    .split(' ')
    .filter((w) => w.length > 0 && !excluded.has(w.toLowerCase()));
  if (words.length === 0) {
    return displayName
      .replace(/[^a-zA-Z]/g, '')
      .slice(0, 2)
      .toUpperCase();
  }
  return words
    .slice(0, 2)
    .map((w) => w[0]!.toUpperCase())
    .join('');
}

export interface CommonLayoutProps {
  /** Version string shown at the bottom of the sidebar. */
  appVersion?: string;
}

/**
 * FIM application chrome: composes the generic {@link AppShell} with role-based
 * menus, the team/school switcher, the notification inbox, the profile menu and
 * the feedback modal. Menu content and layout wiring live in `@suntex.fim/nav`;
 * routing primitives come from `@suntex.core/nav`.
 */
export function CommonLayout({ appVersion = '' }: Readonly<CommonLayoutProps>) {
  const { api } = useFramework();
  const { pathname } = useLocation();
  const navigate = useNavigate();
  const ref = useRef<HTMLDivElement>(null);
  const icons = navIcons;
  const queryClient = useQueryClient();
  const { setPlayerId, signOut, login: user } = useAuth();
  const teamCtx = useOptionalTeamContext();
  const playerCtx = useOptionalPlayerContext();
  const { entityId, entityLevel } = useEntityContext();
  const teamInfo = teamCtx?.teamInfo ?? null;
  const selectedKey = useSelectedKeyFromPath();
  const isAdminRole = user?.entityLevel === EntityLevel.All;
  const [loading, setLoading] = useState(false);
  const [sendMessageModal, setSendMessageModal] = useState(false);

  const loginId = user?.id ?? (0 as LoginId);
  const shouldSkip = !loginId || loginId === 0;
  const { data: notificationCount = { count: 0 } } = useNotificationCount(
    loginId,
    { enabled: !shouldSkip },
  );

  useScrollToTopOnNavigate(ref);

  const loadingCondition = !user || loading;

  useEffect(() => {
    if (loadingCondition) return;
    const raf = requestAnimationFrame(() => {
      window.isCommonLayoutReady = true;
    });
    return () => {
      cancelAnimationFrame(raf);
      window.isCommonLayoutReady = false;
    };
  }, [loadingCondition]);

  if (!user || loadingCondition) return <Spin fullscreen />;

  const authUser = user;
  // Menus and entity-scoped links follow the active entity, not the auth role,
  // so principals/teachers who switch entity get correctly slugged routes.
  const level = entityLevel;
  const roleId =
    level === EntityLevel.Player
      ? (playerCtx?.playerId ?? entityId ?? authUser.entityId)
      : (entityId ?? authUser.entityId);

  async function loadPlayerId() {
    try {
      setLoading(true);
      const response = await api.post_Team_EnsureTeacherPlayer({
        loginId: authUser.id,
        teamId: teamInfo!.id,
      });
      if (!response.playerId) {
        throw new Error('Failed to create teacher player - no player id');
      }
      setPlayerId(response.playerId);
    } catch (error) {
      showErrorMessage(
        'Unable to create teacher player. Please try again or contact support.',
        error,
      );
    } finally {
      setLoading(false);
    }
  }

  async function leaveBetaVersion() {
    await api.post_Login_UpsertLoginMeta({
      loginId: authUser.id,
      metaKey: MetaKey.DashboardConfig,
      data: { generalSettings: { optOutOfBeta: true } },
    });
    window.location.href = 'https://dev.firstinmath.com/team';
  }

  async function handleLogout() {
    await queryClient.cancelQueries();
    await signOut();
    resetDeviceRotateState();
    queryClient.clear();
    navigate('/', { replace: true });
  }

  const { top: menuItems, bottom: bottomMenuItems } = buildMenusForLevel({
    icons,
    level,
    roleId,
    user,
    appVersion,
    onPlay: () => void loadPlayerId(),
  });

  const profileItems = [
    user.entityLevel !== EntityLevel.Player && {
      key: 'my-account',
      label: <Link to="/my-account">My Account</Link>,
    },
    getFlags().leaveBetaVersion &&
      !isAdminRole && {
        key: 'leave-beta',
        label: 'Leave Beta Version',
        onClick: () =>
          Modal.confirm({
            content: 'Leave beta version confirmation message.',
            okText: 'Leave Beta Version',
            cancelText: 'Stay on Beta',
            onOk: leaveBetaVersion,
          }),
      },
    {
      key: 'logout',
      label: 'Sign out',
      onClick: () => void handleLogout(),
    },
  ].filter(Boolean) as MenuProps['items'];

  const showSwitcher =
    user.entityLevel === EntityLevel.School ||
    user.entityLevel === EntityLevel.Team ||
    user.entityLevel === EntityLevel.Player;

  const hasUnread = !!notificationCount.count && notificationCount.count > 0;

  const headerRight = (
    <>
      <Link
        to={entityPath(level, roleId, 'messages')}
        className="inbox"
        aria-label={`Open inbox. ${
          hasUnread
            ? `${notificationCount.count} unread ${
                notificationCount.count === 1 ? 'message' : 'messages'
              }.`
            : 'No unread messages.'
        }`}
      >
        <div className="message-icon">
          <Tooltip
            classNames={{ root: 'progress-tooltip' }}
            arrow={false}
            placement="bottom"
            title={
              hasUnread
                ? `You have ${notificationCount.count} unread messages.`
                : 'You have no unread message.'
            }
          >
            <span>{icons.message}</span>
          </Tooltip>
          {hasUnread && (
            <div className="message-count">{notificationCount.count}</div>
          )}
        </div>
      </Link>
      <Dropdown
        menu={{ items: profileItems }}
        placement="bottomRight"
        trigger={['click']}
        className="dd-profile"
        overlayClassName="dd-profile"
      >
        <Button
          icon={
            <div className="profile-avatar">
              <span>{getInitials(user.displayName || user.userId || '')}</span>
            </div>
          }
          aria-label="Open profile menu"
        >
          {user.displayName || user.userId}
        </Button>
      </Dropdown>
      {getFlags().feedback && !isAdminRole && (
        <Button onClick={() => setSendMessageModal(true)}>
          {icons.feedback} Feedback
        </Button>
      )}
    </>
  );

  return (
    <>
      <AppShell
        hideSidebar={isAdminRole}
        sidebarLoading={teamCtx?.isLoading ?? false}
        logo={
          <Link to={getHomePath(authUser)} aria-label="First In Math Home">
            {getDesktopLogo()}
          </Link>
        }
        collapsedLogo={getCollapsedLogo()}
        expandIcon={icons.menuUnfold}
        collapseIcon={icons.menuFold}
        defaultCollapsed={!pathname.includes('dashboard')}
        collapseBelowWidth={breakpoints.md}
        topMenu={menuItems}
        bottomMenu={bottomMenuItems}
        selectedKeys={[selectedKey]}
        headerLeft={showSwitcher ? <EntitySwitcher /> : null}
        headerRight={headerRight}
        contentRef={ref}
        contentClassName={determineContentClass(pathname)}
      >
        <Outlet />
      </AppShell>
      <SendMessage
        isModalOpen={sendMessageModal}
        btntext="Send Feedback"
        title="Send Feedback"
        sent="URL:"
        closeModal={() => setSendMessageModal(false)}
        selectedUserOptions={[{ label: 'Feedback', value: 0 }]}
      />
    </>
  );
}

function determineContentClass(pathname: string): string {
  const mapping: Record<string, string> = {
    messages: 'msg-layout',
    dashboard: 'dashboard-layout',
    correlations: 'suggest-content-layout',
  };
  const key = Object.keys(mapping).find((route) => pathname.includes(route));
  return mapping[key ?? ''] ?? '';
}
